import { Component, OnDestroy, OnInit, Injectable, ChangeDetectionStrategy } from '@angular/core';
import { PageEvent, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { AlcolockService } from '../../services/alcolock.service';

export interface TurnReportModel {
  id: string;
  timeStamp: string;
  employeeId: string;
  employeeName: string;
}

@Injectable()
export class TurnReportsPaginatorIntl extends MatPaginatorIntl {
  override itemsPerPageLabel = 'Rader per sida:';
  override nextPageLabel = 'Nästa sida';
  override previousPageLabel = 'Föregående sida';
  override firstPageLabel = 'Första sidan';
  override lastPageLabel = 'Sista sidan';

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return `0 av ${length}`;
    }
    const startIndex = page * pageSize;
    const endIndex = startIndex < length ?
      Math.min(startIndex + pageSize, length) :
      startIndex + pageSize;
    return `${startIndex + 1} - ${endIndex} av ${length}`;
  };
}

@Component({
  selector: 'app-turn-reports',
  standalone: false,
  templateUrl: './turanmalningar.component.html',
  styleUrl: './turanmalningar.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: MatPaginatorIntl, useClass: TurnReportsPaginatorIntl }]
})
export class TurnReportsComponent implements OnInit, OnDestroy {
  reports: TurnReportModel[] = [];
  filteredReports: TurnReportModel[] = [];
  paginatedReports: TurnReportModel[] = [];

  selectedTimeRange = 24;
  isLoading = false;
  sortDirection: 'asc' | 'desc' = 'desc';

  searchText: string = '';

  pageSize: number = 10;
  pageIndex: number = 0;
  pageSizeOptions: number[] = [10, 25, 50];

  private destroy$ = new Subject<void>();

  constructor(
    private snackBar: MatSnackBar,
    private alcolockService: AlcolockService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): void {
    this.isLoading = true;
    this.alcolockService.getTestResultsInTimeRange(this.selectedTimeRange, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.reports = results.map(r => ({
            id: r.id,
            timeStamp: r.timeStamp,
            employeeId: r.employeeId,
            employeeName: r.employeeName ?? `ID: ${r.employeeId}`
          }));
          this.applyFilters();
          this.isLoading = false;
        },
        error: () => {
          this.showSnackbar('Kunde inte hämta turanmälningar');
          this.isLoading = false;
        }
      });
  }

  onTimeRangeChange(): void {
    this.loadData();
  }

  applyFilters(): void {
    let filtered = [...this.reports];

    if (this.searchText.trim()) {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(report =>
        report.employeeName.toLowerCase().includes(searchLower) ||
        report.employeeId.toLowerCase().includes(searchLower)
      );
    }

    filtered.sort((a, b) => {
      const timeA = new Date(a.timeStamp).getTime();
      const timeB = new Date(b.timeStamp).getTime();
      return this.sortDirection === 'desc' ? timeB - timeA : timeA - timeB;
    });

    this.filteredReports = filtered;
    this.updatePagination();
  }

  toggleSort(): void {
    this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc';
    this.pageIndex = 0;
    this.applyFilters();
  }

  updatePagination(): void {
    const startIndex = this.pageIndex * this.pageSize;
    this.paginatedReports = this.filteredReports.slice(startIndex, startIndex + this.pageSize);
  }

  onSearchChange(): void {
    this.pageIndex = 0;
    this.applyFilters();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePagination();
  }

  formatTimestamp(timestamp: string): string {
    return new Date(timestamp).toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  showSnackbar(message: string): void {
    this.snackBar.open(message, 'Stäng', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: 'snackbar-container'
    });
  }

}
